import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Camera } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { BrandPeriod, UnmetGroup, useBrandApi } from '../../../common/hooks/use-brand-api.hook';
import { EmptyState, PillTabs, SectionCard } from '../../../components/design-system/admin';
import { InlineTrend, Thumb } from '../../../components/design-system/analytics';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { BrandPage, Change, LoadFailed, PeriodPills, PrivacyFootnote, useBrandMe } from '../../../modules/brand/components/BrandUi';
import { countryName, vocab } from '../../../modules/brand/brand-format';

/** Swatches for the colour families — colour here IS the information. */
const SWATCH: Record<string, string> = {
    white: '#F4F2EE', beige: '#D9C7A7', brown: '#7A5534', grey: '#8E8E8A', black: '#1F1F1F', blue: '#3D6BA8', green: '#4F7D5A',
    red: '#A8443A', orange: '#D07A35', yellow: '#D9B740', pink: '#D99AA5', purple: '#7A5A9A', metallic: '#B8B4AA', multicolour: 'conic-gradient(#A8443A, #D9B740, #4F7D5A, #3D6BA8, #A8443A)',
};

/**
 * What shoppers photographed with Snap & Find and no shop could match —
 * demand nobody serves, written by shoppers themselves — grouped by what it
 * looked like, with the brand's nearest product to each.
 */
export function BrandUnmetPage() {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const api = useBrandApi();
    const { me } = useBrandMe();
    const [period, setPeriod] = useState<BrandPeriod>('30d');
    const [groupBy, setGroupBy] = useState<'look' | 'country' | 'city'>('look');
    const { data, loading, error, reload } = useLoader(async () => (me?.connected ? api.unmet(period, groupBy) : null), [period, groupBy, me?.connected]);

    const look = (g: UnmetGroup) =>
        [g.colorFamily && vocab(t, g.colorFamily), g.appearance && t('BRAND_PORTAL.UNMET.LOOK', { look: vocab(t, g.appearance).toLowerCase() })].filter(Boolean).join(' ') || t('BRAND_PORTAL.COMMON.NOT_KNOWN');
    const where = (g: UnmetGroup) => [g.city, g.country && countryName(g.country, lang)].filter(Boolean).join(', ');

    return (
        <BrandPage
            testId="brand-unmet"
            crumb={t('BRAND_PORTAL.NAV.UNMET')}
            title={t('BRAND_PORTAL.UNMET.TITLE')}
            description={t('BRAND_PORTAL.UNMET.SUBTITLE')}
            actions={<PeriodPills value={period} onChange={setPeriod} />}
        >
            <PillTabs
                value={groupBy}
                onChange={setGroupBy}
                ariaLabel={t('BRAND_PORTAL.DEMAND.GROUP_BY')}
                options={(['look', 'country', 'city'] as const).map((g) => ({ value: g, label: t(`BRAND_PORTAL.UNMET.BY_${g.toUpperCase()}`) }))}
            />
            {error && <LoadFailed onRetry={reload} />}
            {data && !data.closestAvailable && (
                <Card className="py-3" data-testid="brand-unmet-surfaceid-off">
                    <CardContent className="text-sm text-muted-foreground">{t('BRAND_PORTAL.UNMET.SURFACE_ID_OFF')}</CardContent>
                </Card>
            )}
            {data && (
                data.groups.length ? (
                    <SectionCard
                        title={t('BRAND_PORTAL.UNMET.TOTAL', { count: data.total ?? 0 })}
                        description={t('BRAND_PORTAL.UNMET.TOTAL_TEXT')}
                        testId="brand-unmet-groups"
                    >
                        <ul className="flex flex-col divide-y">
                            {data.groups.map((g) => (
                                <li key={g.key} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center">
                                    <span
                                        className="size-10 shrink-0 rounded-lg border"
                                        style={{ background: SWATCH[g.colorFamily ?? ''] ?? 'var(--muted)' }}
                                        aria-hidden
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium">
                                            {look(g)}
                                            {where(g) && <span className="font-normal text-muted-foreground"> · {where(g)}</span>}
                                        </p>
                                        <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                            <span>{t('BRAND_PORTAL.UNMET.SEARCHES', { count: g.searches })}</span>
                                            {g.previous != null && <Change value={g.change} />}
                                            {g.previous === null && <span>{t('BRAND_PORTAL.UNMET.PREVIOUS_WITHHELD')}</span>}
                                            <span>· {t('BRAND_PORTAL.UNMET.ACROSS', { count: g.retailers })}</span>
                                        </p>
                                    </div>
                                    <InlineTrend data={g.weekly.map((w) => w.searches)} width={72} height={22} />
                                    <div className="min-w-0 sm:w-64">
                                        {g.closest ? (
                                            <Link to={`/brand/products/${g.closest.uuid}`} className="flex items-center gap-2 rounded-md p-1 hover:bg-accent/40" data-testid="brand-unmet-closest">
                                                <Thumb src={g.closest.thumbnailUrl} size="sm" />
                                                <span className="min-w-0">
                                                    <span className="block truncate text-xs text-muted-foreground">{t('BRAND_PORTAL.UNMET.YOUR_CLOSEST')}</span>
                                                    <span className="block truncate text-sm">{g.closest.name}</span>
                                                </span>
                                                <span className="ms-auto text-xs tabular-nums text-muted-foreground">{g.closest.similarity.toFixed(2)}</span>
                                            </Link>
                                        ) : (
                                            <p className="text-xs text-muted-foreground">{t(data.closestAvailable ? 'BRAND_PORTAL.UNMET.NO_CLOSEST' : 'BRAND_PORTAL.UNMET.CLOSEST_OFF')}</p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {data.other && <p className="mt-4 text-xs text-muted-foreground">{t('BRAND_PORTAL.UNMET.OTHER', { count: data.other.searches })}</p>}
                    </SectionCard>
                ) : (
                    <Card data-testid="brand-unmet-empty">
                        <CardContent>
                            <EmptyState icon={<Camera />} title={t('BRAND_PORTAL.UNMET.EMPTY_TITLE')} description={t('BRAND_PORTAL.UNMET.EMPTY_TEXT')} />
                        </CardContent>
                    </Card>
                )
            )}
            {loading && !data && <p className="text-sm text-muted-foreground">{t('BRAND_PORTAL.COMMON.LOADING')}</p>}
            <PrivacyFootnote />
            <p className="text-xs text-muted-foreground">{t('BRAND_PORTAL.UNMET.PHOTO_NOTE')}</p>
        </BrandPage>
    );
}

export default BrandUnmetPage;
